import PropTypes from "prop-types";
import { Alert, Box, Stack, Typography } from "@mui/material";
import ActionButton from "src/pages/cases/ActionButton";
import { typeLabel } from "./pitfalls";

/**
 * A form row the way the standard asks for it: the label above the field and
 * always visible, an optional hint under the label, and the error as a plain
 * sentence under the field. Pass the input as the child, with id={id}.
 */
export const FormField = ({ id, label, optional, hint, error, children }) => (
  <Box>
    <Typography component="label" id={`${id}-label`} htmlFor={id} variant="subtitle2" sx={{ display: "block", fontWeight: 600, mb: 0.5 }}>
      {label}
      {optional ? (
        <Typography component="span" variant="subtitle2" color="text.secondary" sx={{ fontWeight: 400 }}>
          {" "}
          (optional)
        </Typography>
      ) : null}
    </Typography>
    {hint ? (
      <Typography id={`${id}-hint`} variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
        {hint}
      </Typography>
    ) : null}
    {children}
    {error ? (
      <Typography id={`${id}-error`} role="alert" variant="body2" color="error.main" sx={{ mt: 0.5 }}>
        {error}
      </Typography>
    ) : null}
  </Box>
);

FormField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.node.isRequired,
  optional: PropTypes.bool,
  hint: PropTypes.node,
  error: PropTypes.node,
  children: PropTypes.node,
};

/**
 * Props for a select (TextField select) inside FormField. MUI points the
 * select's aria-labelledby at itself when the TextField has no label prop,
 * so screen readers announced the value but not the question; point it at
 * the FormField label and its hint and error instead.
 */
export const selectA11y = (id, { hint, error }) => ({
  displayEmpty: true,
  SelectDisplayProps: {
    "aria-labelledby": `${id}-label`,
    "aria-describedby": describedBy(id, { hint, error }),
  },
});

/** describedby for an input inside FormField, so hints and errors are read out. */
export const describedBy = (id, { hint, error }) =>
  [hint ? `${id}-hint` : null, error ? `${id}-error` : null].filter(Boolean).join(" ") || undefined;

/**
 * After a one-click fix, put focus on the field it changed (the fix button
 * itself disappears with the warning, which dropped focus to <body>).
 * `ids` maps a field name to the id of its input.
 */
export const focusFixedField = (fix, ids) => {
  const key = Object.keys(fix).find((name) => ids[name]);
  if (!key) return;
  setTimeout(() => document.getElementById(ids[key])?.focus(), 0);
};

/** The pitfall warnings from ./pitfalls, each with its one-click fix. */
export const PitfallList = ({ items, onFix }) => {
  if (!items?.length) return null;
  return (
    <Stack spacing={1} aria-live="polite">
      {items.map((item) => (
        <Alert
          key={item.key}
          severity={item.severity === "info" ? "info" : "warning"}
          action={
            item.fix && onFix ? (
              <ActionButton variant="text" onClick={() => onFix(item.fix)}>
                {fixLabel(item.fix)}
              </ActionButton>
            ) : null
          }
        >
          {item.text}
        </Alert>
      ))}
    </Stack>
  );
};

PitfallList.propTypes = {
  items: PropTypes.array,
  onFix: PropTypes.func,
};

function fixLabel(fix) {
  if (fix.type) return `Use ${typeLabel(fix.type)}`;
  if (fix.is_required === false) return "Make optional";
  if (fix.required_validation === false) return "Turn off";
  if (fix.list_name) return "Capitalise";
  return "Fix";
}
